"use client";
import Image from "next/image";
import Reveal from "../../components/Reveal";
import { motion } from "framer-motion";
import { useTranslations } from "next-intl";

const MarketSummarySection = () => {
  const t = useTranslations("invester");
  const globalMarket = [
    {
      title: t("ai-gdp-contribution-2030"),
      value: "$15.7T",
    },
    {
      title: t("gpu-cost-growth-22-25"),
      value: "×5",
    },
    {
      title: t("depin-expected-cagr"),
      value: "60–70%",
    },
    {
      title: t("ai-cloud-spend-2030"),
      value: ">$1T",
    },
    {
      title: t("expected-cagr-of-depin"),
      value: "~$20B",
    },
  ];

  const hyraLiveStats = [
    {
      title: t("devices-connected"),
      value: "2,700,723",
    },
    {
      title: t("countries-covered"),
      value: "205+",
    },
    {
      title: t("transactions-day"),
      value: "322,382",
    },
    {
      title: t("active-nodes"),
      value: "600,082",
    },
    {
      title: t("gpu-tpu-hours"),
      value: "1,251,647 hrs",
    },
  ];

  return (
    <section className="mx-auto max-w-[1440px] px-[78px] py-[78px] max-lg:px-4 max-sm:py-[56px]">
      <div className="mx-auto max-w-[844px] text-center">
        <p className="mb-4 mt-14 font-open-sans text-base font-normal text-[#06f]">
          AI & Depin
        </p>
        <Reveal
          delay={0.1}
          duration={0.8}
          direction="left"
          withBlur={true}
          blurAmount={8}
        >
          <h2 className="text-[48px]/[56px] tracking-[-1px] max-lg:text-[32px]/[40px]">
            {t("market-summary-macro-growth-hyra-live-traction")}
          </h2>
        </Reveal>

        <Reveal
          delay={0.5}
          duration={0.8}
          direction="left"
          withBlur={true}
          blurAmount={8}
        >
          <p className="mt-6 text-base font-light text-[#646464]">
            {t(
              "macro-opportunity-on-the-left-real-time-hyra-network-stats-on-the-right-updated-9-11-2025-4-34-02-pm",
            )}
          </p>
        </Reveal>
      </div>
      <div className="relative mt-20 flex justify-between max-lg:gap-6 max-sm:gap-4">
        <div className="pointer-events-none absolute inset-0 -z-10 flex w-full justify-center">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="object-cover object-center"
            style={{
              WebkitMaskImage:
                "radial-gradient(ellipse at center, rgba(0,0,0,1) 50%, transparent 100%)",
              WebkitMaskRepeat: "no-repeat",
              WebkitMaskPosition: "center",
              WebkitMaskSize: "cover",
              maskImage:
                "radial-gradient(ellipse at center, rgba(0,0,0,1) 50%, transparent 100%)",
              maskRepeat: "no-repeat",
              maskPosition: "center",
              maskSize: "cover",
            }}
          >
            <source
              src="https://cdn-r2.hyra.network/hyra-network/gloabal.webm"
              type="video/webm"
            />
          </video>
        </div>
        <div className="w-1/2 max-w-[382px]">
          <motion.div
            initial={{ opacity: 0, translateX: -40 }}
            whileInView={{ opacity: 1, translateX: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            <div className="mb-10 flex items-center justify-center gap-2 max-lg:flex-col">
              <Image
                src={"/images/icons/dot-summary.svg"}
                width={20}
                height={20}
                alt="icon"
              />
              <p className="text-[32px]/[40px] tracking-[-1px] max-lg:text-[20px]/[28px] max-sm:min-h-[55px] max-sm:text-center">
                {t("global-market-overview")}
              </p>
            </div>
          </motion.div>
          <div className="flex flex-col gap-4">
            {globalMarket.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 0.6,
                  ease: "easeOut",
                  delay: index * 0.2,
                }}
                viewport={{ once: true }}
                className="rounded-3xl bg-[#F9F9F9B2] p-6 max-sm:min-h-[156px] max-sm:p-3"
              >
                <p className="text-base max-md:text-center max-sm:text-start max-sm:text-xs">
                  {item.title}
                </p>
                <div className="mt-6 flex justify-between max-md:w-full max-md:flex-col max-md:items-center max-md:gap-6">
                  <p className="w-[60%] text-[32px]/[40px] font-semibold max-md:w-full max-md:text-center max-sm:text-[22px]/[30px]">
                    {item.value}
                  </p>
                  <div className="relative h-[22px] w-[123px] max-lg:w-[90px] max-md:w-full">
                    <Image src={"/images/icons/chart.svg"} fill alt="chart" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="w-1/2 max-w-[382px]">
          <motion.div
            initial={{ opacity: 0, translateX: 40 }}
            whileInView={{ opacity: 1, translateX: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            <div className="mb-10 flex items-center justify-center gap-2 max-lg:flex-col">
              <Image
                src={"/images/icons/dot-summary.svg"}
                width={20}
                height={20}
                alt="icon"
              />
              <p className="text-[32px]/[40px] tracking-[-1px] max-lg:text-[20px]/[28px] max-sm:min-h-[55px] max-sm:text-center">
                {t("hyra-live-stats")}
              </p>
            </div>
          </motion.div>

          <div className="flex flex-col gap-4">
            {hyraLiveStats.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 0.6,
                  ease: "easeOut",
                  delay: index * 0.2,
                }}
                viewport={{ once: true }}
                className="rounded-3xl bg-[#F9F9F9B2] p-6 max-sm:min-h-[156px] max-sm:p-3"
              >
                <p className="text-base max-md:text-center max-sm:text-start max-sm:text-xs">
                  {item.title}
                </p>
                <div className="mt-6 flex justify-between max-md:w-full max-md:flex-col max-md:items-center max-md:gap-6">
                  <p className="w-[60%] text-[32px]/[40px] font-semibold max-md:w-full max-md:text-center max-sm:text-[22px]/[30px]">
                    {item.value}
                  </p>
                  <div className="relative h-[22px] w-[123px] max-lg:w-[90px] max-md:w-full">
                    <Image src={"/images/icons/chart.svg"} fill alt="chart" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default MarketSummarySection;
